import { FC, useEffect, useRef, useState } from 'react';
import { ChevronDown, GraduationCap } from 'lucide-react';

import type { PlanCourse, RequirementGroup } from '@teachur/http-client';

import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { cn } from '@new/lib';

import { CompletedPlan } from '../CompletedPlan/CompletedPlan';
import { DegreePlanProgressBar } from '../DegreePlanProgressBar/DegreePlanProgressBar';
import { GeneralEducationCard } from '../GeneralEducationCard/GeneralEducationCard';
import { RequirementSection } from '../RequirementSection/RequirementSection';
import { AddCourseBuilderModal } from '../AddCourseBuilderModal/AddCourseBuilderModal';
import { usePlanPicker } from '../../hooks/usePlanPicker';
import { groupAnchorId, groupPlan, isPlanComplete, sumCredits } from '../../lib/planView';
import {
  addCoursesToPlan,
  collectCourseIds,
  removeCourseFromPlan,
  switchCourseInPlan,
} from '../../lib/planEdits';
import type { PlanDraft } from '../../hooks/usePlanForm';

interface PlanProps {
  plan: PlanDraft;
  onChange: (plan: PlanDraft) => void;
  sharedCourseIds?: ReadonlySet<number>;
}

export const Plan: FC<PlanProps> = ({ plan, onChange, sharedCourseIds }) => {
  const { degree, groups } = plan;
  const grouped = groupPlan({ groups });
  const courseIds = collectCourseIds(groups);
  const picker = usePlanPicker({
    onSwitch: (groupId, replacingKey, option) =>
      onChange(switchCourseInPlan(plan, groupId, replacingKey, option)),
    onAdd: (groupId, options) => onChange(addCoursesToPlan(plan, groupId, options)),
  });
  const hasGeneralEducation = grouped.generalEducation.length > 0;
  const planComplete = isPlanComplete(groups);

  const [expanded, setExpanded] = useState(false);
  const [pendingSharedAction, setPendingSharedAction] = useState<(() => void) | null>(null);
  const pendingScrollGroupId = useRef<string | null>(null);

  const selectGroup = (groupId: string) => {
    if (expanded) {
      document
        .getElementById(groupAnchorId(groupId))
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    pendingScrollGroupId.current = groupId;
    setExpanded(true);
  };

  const creditsRequired = groups.reduce((total, group) => total + group.requiredCredits, 0);
  const creditsSelected = sumCredits(groups.flatMap((group) => group.courses));
  const creditsRemaining = Math.max(creditsRequired - creditsSelected, 0);

  const guardSharedCourse = (course: PlanCourse, action: () => void) => {
    if (course.courseId != null && sharedCourseIds?.has(course.courseId)) {
      setPendingSharedAction(() => action);
      return;
    }
    action();
  };
  const confirmSharedAction = () => {
    const action = pendingSharedAction;
    setPendingSharedAction(null);
    action?.();
  };

  const switchInGroup = (group: RequirementGroup, course: PlanCourse) =>
    guardSharedCourse(course, () => picker.openSwitch(group, course));

  const removeFromGroup = (group: RequirementGroup, course: PlanCourse) =>
    guardSharedCourse(course, () => onChange(removeCourseFromPlan(plan, group.id, course)));

  useEffect(() => {
    if (!expanded || pendingScrollGroupId.current == null) {
      return;
    }
    const groupId = pendingScrollGroupId.current;
    pendingScrollGroupId.current = null;
    document
      .getElementById(groupAnchorId(groupId))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [expanded]);

  if (planComplete) {
    return <CompletedPlan name={degree.name} credits={creditsRequired} groups={groups} />;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-(--neutral-black-NB200) bg-(--neutral-white-NW1000)">
      <div className="flex items-stretch">
        <div className="flex min-w-0 flex-1 flex-col gap-4 px-5 py-5 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-(--primary-O300)">
              <GraduationCap className="size-5 text-(--primary-O1100)" />
            </span>
            <span className="[font-size:var(--fs-p3)]! font-semibold text-(--neutral-black-NB900)!">
              {degree.name}
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="m-0! leading-none">
              <span className="[font-size:var(--fs-h4)]! font-bold text-(--neutral-black-NB900)!">
                {creditsSelected}
              </span>
              <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
                {' '}
                of {creditsRequired} credits selected
              </span>
            </p>
            <p className="m-0! leading-none">
              <span className="[font-size:var(--fs-l5)]! font-semibold text-(--neutral-black-NB800)!">
                {creditsRemaining}
              </span>
              <span className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB600)!">
                {' '}
                credits left to choose
              </span>
            </p>
          </div>

          <DegreePlanProgressBar groups={groups} onSelectGroup={selectGroup} />
        </div>

        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="flex w-28 shrink-0 cursor-pointer flex-col items-center justify-center gap-2 border-l border-(--primary-O300) bg-(--primary-O200) sm:w-36"
        >
          <span className="[font-size:var(--fs-l5)]! font-semibold text-(--primary-O1100)!">
            View plan
          </span>
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-(--primary-O400) bg-(--neutral-white-NW1000)">
            <ChevronDown
              className={cn(
                'size-4 text-(--primary-O1100) transition-transform',
                expanded && 'rotate-180'
              )}
            />
          </span>
        </button>
      </div>

      {expanded ? (
        <div className="flex flex-col gap-4 border-t border-(--neutral-black-NB200) p-4 sm:p-5">
          {hasGeneralEducation ? (
            <GeneralEducationCard groups={grouped.generalEducation} onSwitch={switchInGroup} />
          ) : null}

          {grouped.required.map((group) => (
            <RequirementSection key={group.id} group={group} />
          ))}

          {grouped.electives.map((group) => (
            <RequirementSection
              key={group.id}
              group={group}
              title="General Electives"
              emptyText="Add the elective courses you want to take."
              editable
              onSwitch={switchInGroup}
              onRemove={removeFromGroup}
              onAdd={picker.openAdd}
            />
          ))}

          <AddCourseBuilderModal
            {...picker.modalProps}
            excludedCourseIds={courseIds}
            onClose={picker.close}
            onConfirm={picker.confirm}
          />

          <ConfirmationModal
            isOpen={pendingSharedAction != null}
            title="This course counts for both degrees"
            description="This course is also part of your bachelor's plan — completing it in your associate degree covers both requirements. If you switch or remove it here, you'll still need to take it later for your bachelor's degree."
            confirmText="Continue anyway"
            cancelText="Keep course"
            onConfirm={confirmSharedAction}
            onClose={() => setPendingSharedAction(null)}
          />
        </div>
      ) : null}
    </div>
  );
};
